import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Box, CircularProgress } from '@mui/material'
import PropTypes from 'prop-types'

import {
    quoteItemAttachmentsApi,
    quoteItemAttachmentsPath,
    useDeleteQuoteItemAttachmentMutation,
    useGetQuoteItemAttachmentsQuery,
    useLazyGetQuoteItemAttachmentDownloadUrlQuery,
    useSetQuoteItemAttachmentVisibilityMutation,
} from '@/app/services/quoteItemAttachments'
import { uploadAttachmentFile } from '@/app/services/uploadAttachmentFile'

import AttachmentsPanel from './AttachmentsPanel'

/**
 * QuoteItemAttachmentsPanel - The documents ledger for one quote item. Rows come from the item's
 * own list endpoint, fetched only while this panel is mounted, so a quote with many items issues
 * no per-item request until someone opens one. Rendering is gated by the caller.
 *
 * @param {Object} props
 * @param {string} props.organisationId
 * @param {string} props.quoteId
 * @param {string} props.quoteItemId
 */
const QuoteItemAttachmentsPanel = ({ organisationId, quoteId, quoteItemId }) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const parent = { organisationId, quoteId, quoteItemId }

    // No polling: a pending row shows its scan result after a write in this panel, a reopen or a page reload.
    // Fetch on every open: a refresh that failed while the dialog was open, or an upload that
    // finished after it closed, leaves the cached list behind the server.
    const { data: attachments, isLoading } = useGetQuoteItemAttachmentsQuery(parent, {
        refetchOnMountOrArgChange: true,
    })

    const [setVisibility] = useSetQuoteItemAttachmentVisibilityMutation()
    const [deleteAttachment, { isLoading: isDeleting }] = useDeleteQuoteItemAttachmentMutation()
    const [fetchDownloadUrl] = useLazyGetQuoteItemAttachmentDownloadUrlQuery()

    // The link is minted per click. A refusal (still pending, or failed) means there is nothing
    // to open, and the panel says so.
    const resolveDownloadUri = async ({ documentId }) => {
        try {
            return await fetchDownloadUrl({ ...parent, documentId }).unwrap()
        } catch {
            return undefined
        }
    }

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress
                    aria-label={t('Loading attachments')}
                    color="secondary"
                />
            </Box>
        )
    }

    return (
        <AttachmentsPanel
            attachments={attachments ?? []}
            emptyMessage={t('No attachments on this part yet.')}
            isDeleting={isDeleting}
            organisationId={organisationId}
            resolveDownloadUri={resolveDownloadUri}
            title={t('Part attachments')}
            onDelete={(documentId) => deleteAttachment({ ...parent, documentId }).unwrap()}
            onSetVisibility={(documentId, isPublic) => setVisibility({ ...parent, documentId, isPublic }).unwrap()}
            onUploadFile={(file, options) =>
                uploadAttachmentFile({ path: quoteItemAttachmentsPath(parent), file, ...options })
            }
            onUploadsSettled={() =>
                dispatch(
                    quoteItemAttachmentsApi.endpoints.getQuoteItemAttachments.initiate(parent, {
                        forceRefetch: true,
                        subscribe: false,
                    })
                )
            }
        />
    )
}

QuoteItemAttachmentsPanel.propTypes = {
    organisationId: PropTypes.string.isRequired,
    quoteId: PropTypes.string.isRequired,
    quoteItemId: PropTypes.string.isRequired,
}

export default QuoteItemAttachmentsPanel
